"use client";

import { Button } from "@/components/base/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/base/card";
import { Checkbox } from "@/components/base/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/base/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/base/dropdown-menu";
import { Input } from "@/components/base/input";
import { Label } from "@/components/base/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/base/select";
import { formatCurrency } from "@/lib/currency";
import {
  customElementAttributeMappers,
  setCustomElementMetadata,
} from "@/lib/customElements";
import { cn } from "@/lib/utils";
import { QuoteCalculator } from "@/models";
import {
  EagleViewProduct,
  EagleViewReport,
  PlaceOrderRequest,
} from "@/models/eagleview";
import { QuoteService } from "@/services";
import { EagleViewService } from "@/services/eagleview";
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  Download,
  FileText,
  FileUp,
  UploadCloud,
  UploadIcon,
  X,
} from "lucide-react";
import * as React from "react";
import { useMemo } from "react";

interface OrderAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
}

interface TakeoffImporterProps {
  apiUrl?: string;
  apiStage?: string;
  jobId?: number;
  eagleViewAt?: string;
  eagleViewRt?: string;
  placeholderText?: string;
  emptyMessage?: string;
  width?: string;
  onOrderComplete?: (reportIds: number[]) => void;
  onHoverFileSelected?: (file: File) => void;
  style?: React.CSSProperties;
  orderAddress?: OrderAddress;
}

export function TakeoffImporter({
  apiUrl = "https://api.example.com",
  apiStage = "dev",
  jobId = undefined,
  eagleViewAt = undefined,
  eagleViewRt = undefined,
  placeholderText = "Import takeoff data...",
  emptyMessage = "No options found.",
  width = undefined,
  onOrderComplete,
  onHoverFileSelected,
  style = {},
  orderAddress = undefined,
}: Readonly<TakeoffImporterProps>) {
  const enableEagleView = apiStage === "dev";
  const eagleViewSandbox = true;

  const eagleViewSandboxAddress = useMemo(() => {
    return {
      Address: "1240 MAPLE RD",
      City: "ELMA",
      State: "NY",
      Zip: "14059",
      Country: "US",
      Latitude: 42.8022826,
      Longitude: -78.621854,
      AddressType: 1,
    };
  }, []);

  const eagleViewSandboxProductId = 31;
  const eagleViewSandboxReportId = 53863159;

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [products, setProducts] = React.useState<EagleViewProduct[]>([]);
  const [selectedProduct, setSelectedProduct] =
    React.useState<EagleViewProduct | null>(null);
  const [eagleViewReports, setEagleViewReports] = React.useState<
    EagleViewReport[]
  >([]);
  const [selectedAddOns, setSelectedAddOns] = React.useState<number[]>([]);
  const [quoteCalculator, setQuoteCalculator] =
    React.useState<QuoteCalculator>();
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const confirmCardRef = React.useRef<HTMLDivElement>(null);
  const eagleViewService = React.useMemo(
    () => new EagleViewService(apiUrl, apiStage, eagleViewAt, eagleViewRt),
    [apiUrl, apiStage, eagleViewAt, eagleViewRt]
  );

  const quoteService = React.useMemo(
    () => new QuoteService(apiUrl, apiStage),
    [apiUrl, apiStage]
  );

  const fetchEagleViewReports = React.useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      const reportIds = JSON.parse(
        (quoteCalculator?.qcevreportids || "[]") as string
      ) as string[];
      const reports = await Promise.all(
        reportIds.map((id) =>
          eagleViewService.getReport(
            eagleViewSandbox ? eagleViewSandboxReportId : +id
          )
        )
      );
      const reportFiles = await Promise.all(
        reportIds.map((id) =>
          eagleViewService.getReportFile(
            eagleViewSandbox ? eagleViewSandboxReportId : +id
          )
        )
      );

      console.log("reportFiles", reportFiles);
      setEagleViewReports(reports);
    } catch (error) {
      console.error("Failed to fetch EagleView reports:", error);
    } finally {
      setLoading(false);
    }
  }, [quoteCalculator, eagleViewService, jobId, eagleViewSandbox]);

  const createQuoteCalculator = React.useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      const quoteCalculator = await quoteService.createQuoteCalculator({
        qcjid: jobId,
      });
      setQuoteCalculator(quoteCalculator);
      // Handle the fetched quote calculator data as needed
    } catch (error) {
      console.error("Failed to create Quote Calculator:", error);
    } finally {
      setLoading(false);
    }
  }, [quoteService, jobId]);

  const fetchQuoteCalculator = React.useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      const quoteCalculator = await quoteService.getQuoteCalculatorByJobId(
        jobId
      );

      if (!quoteCalculator) {
        createQuoteCalculator();
        return;
      }

      setQuoteCalculator(quoteCalculator);
      // Handle the fetched quote calculator data as needed
    } catch (error) {
      console.error("Failed to fetch Quote Calculator:", error);
    } finally {
      setLoading(false);
    }
  }, [quoteService, jobId, createQuoteCalculator]);

  const fetchProducts = React.useCallback(async () => {
    if (products.length > 0) return;

    try {
      setLoading(true);
      const fetchedProducts = await eagleViewService.getAvailableProducts();
      setProducts(fetchedProducts);
    } catch (error) {
      console.error("Failed to fetch EagleView products:", error);
    } finally {
      setLoading(false);
    }
  }, [eagleViewService, products.length]);

  // Load quote calculator when component mounts
  React.useEffect(() => {
    fetchQuoteCalculator();
  }, [fetchQuoteCalculator]);

  // Load quote calculator when component mounts
  React.useEffect(() => {
    fetchEagleViewReports();
  }, [fetchEagleViewReports]);

  // Load products when component mounts
  React.useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "application/json") {
      console.log("Selected Hover JSON file:", file);
      onHoverFileSelected?.(file);
    }
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // const handleTriggerFileInput = () => {
  //   fileInputRef.current?.click();
  //   setOpen(false);
  // };

  const handleProductSelect = (product: EagleViewProduct) => {
    setSelectedProduct(product);
    setSelectedAddOns([]); // Reset selected add-ons when selecting a new product
    setShowConfirmDialog(true);
    setOpen(false);
  };

  const handleAddOnToggle = (addOnId: number | null | undefined) => {
    if (addOnId === undefined || addOnId === null) return;
    setSelectedAddOns((current) => {
      // If the clicked add-on is already selected, deselect it
      if (current.includes(addOnId)) {
        return [];
      } else {
        // Otherwise, select only the clicked add-on
        return [addOnId];
      }
    });
  };

  const handlePlaceOrder = React.useCallback(async () => {
    if (!selectedProduct || !orderAddress) return;

    try {
      setLoading(true);

      // Create order request object
      const orderData: PlaceOrderRequest = {
        OrderReports: [
          {
            ReportAddresses: [
              eagleViewSandbox
                ? eagleViewSandboxAddress
                : {
                    Address: orderAddress.address,
                    City: orderAddress.city,
                    State: orderAddress.state,
                    Zip: `${orderAddress.zip}`,
                    Latitude: +orderAddress.latitude,
                    Longitude: +orderAddress.longitude,
                    AddressType: 4,
                  },
            ],
            PrimaryProductId: eagleViewSandbox
              ? eagleViewSandboxProductId
              : selectedProduct.productID || 0,
            DeliveryProductId:
              selectedProduct.deliveryProducts?.[0]?.productID || 0,
            MeasurementInstructionType: 2,
            ChangesInLast4Years: false,
            AddOnProductIds: eagleViewSandbox
              ? []
              : selectedAddOns.length > 0
              ? selectedAddOns
              : undefined,
          },
        ],
      };

      // const priceOrderResponse = await eagleViewService.priceOrder(orderData);

      const placeOrderResponse = await eagleViewService.placeOrder(orderData);

      if (!quoteCalculator) {
        await createQuoteCalculator();
      }

      // Call the callback with the report IDs
      if (
        placeOrderResponse.ReportIds &&
        placeOrderResponse.ReportIds.length > 0 &&
        quoteCalculator?.qcid
      ) {
        let existingReportIds: string[] = [];
        try {
          existingReportIds = quoteCalculator.qcevreportids
            ? (JSON.parse(quoteCalculator.qcevreportids) as string[])
            : [];
        } catch (error) {
          console.error(error);
        }

        // Update the quote calculator with the report IDs
        const updatedQuoteCalculator = {
          ...quoteCalculator,
          qcid: quoteCalculator.qcid,
          qcevreportids: JSON.stringify(
            Array.from(
              new Set([...existingReportIds, ...placeOrderResponse.ReportIds])
            )
          ),
        };
        await quoteService.updateQuoteCalculator(
          quoteCalculator.qcid,
          updatedQuoteCalculator
        );
        onOrderComplete?.(placeOrderResponse.ReportIds);
      }

      fetchEagleViewReports();
      setShowConfirmDialog(false);
      setSelectedProduct(null);
      setSelectedAddOns([]);
    } catch (error) {
      console.error("Failed to place EagleView order:", error);
    } finally {
      setLoading(false);
    }
  }, [
    createQuoteCalculator,
    fetchEagleViewReports,
    eagleViewService,
    onOrderComplete,
    orderAddress,
    quoteCalculator,
    quoteService,
    selectedAddOns,
    selectedProduct,
    eagleViewSandbox,
    eagleViewSandboxProductId,
    eagleViewSandboxAddress,
  ]);

  const calculateTotal = () => {
    if (!selectedProduct) return 0;

    const productPrice = selectedProduct.priceMin || 0;
    const deliveryPrice = selectedProduct.deliveryProducts?.[0]?.priceMin || 0;

    // Add the prices of selected add-ons
    const addOnPrice =
      selectedProduct.addOnProducts
        ?.filter((addOn) => selectedAddOns.includes(addOn.productID || 0))
        .reduce((sum, addOn) => sum + (addOn.priceMin || 0), 0) || 0;

    return productPrice + deliveryPrice + addOnPrice;
  };

  const handleProductChange = (productId: string) => {
    const newProduct = products.find(
      (p) => p.productID === parseInt(productId, 10)
    );
    if (newProduct) {
      setSelectedProduct(newProduct);
      setSelectedAddOns([]); // Reset selected add-ons when changing product
    }
  };

  // Scroll to the confirmation card when it's shown
  React.useEffect(() => {
    if (showConfirmDialog && confirmCardRef.current) {
      // Small delay to ensure the card is rendered
      setTimeout(() => {
        confirmCardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [showConfirmDialog]);

  const measurementStringToNumOrNull = (
    measurement: string | null | undefined
  ): number | null => {
    if (measurement === null || measurement === undefined) {
      return null;
    }
    const num = parseFloat(measurement.split(" ")[0]);
    return isNaN(num) ? null : num;
  };

  const syncMeasurements = (report: EagleViewReport) => {
    const measurements = {
      sqf: measurementStringToNumOrNull(report.Area),
      lfe: measurementStringToNumOrNull(report.LengthEave),
      tlfh: measurementStringToNumOrNull(report.LengthHip),
      lfr: measurementStringToNumOrNull(report.LengthRake),
      tlfr: measurementStringToNumOrNull(report.LengthRidge),
      tlfv: measurementStringToNumOrNull(report.LengthValley),
      dpitch: report.Pitch ?? "",
    };

    Object.keys(measurements).forEach((key) => {
      const input = document.getElementById(key) as
        | HTMLInputElement
        | undefined;

      if (!input) return;

      input.value =
        measurements[key as keyof typeof measurements]?.toString() || "";
    });
  };

  return (
    <div className="relative">
      {/* Dropdown menu for importing takeoff data */}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            className={cn(width ? `w-[${width}]` : "w-auto")}
            style={style}
          >
            {placeholderText}
            <ChevronDown className="w-4 h-4 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="p-0" align="start">
          <Command>
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup heading="Import Options">
                {orderAddress && enableEagleView && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <div className="flex items-center">
                        <UploadCloud className="w-4 h-4 mr-2" />
                        <span>Order EagleView</span>
                      </div>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent
                      className="p-0"
                      sideOffset={2}
                      alignOffset={0}
                    >
                      <Command className="min-w-[220px]">
                        <CommandList>
                          <CommandGroup heading="EagleView Products">
                            {loading ? (
                              <div className="px-4 py-2 text-sm text-muted-foreground">
                                Loading...
                              </div>
                            ) : products.length > 0 ? (
                              products.map((product) => (
                                <CommandItem
                                  key={product.productID}
                                  onSelect={() => handleProductSelect(product)}
                                >
                                  <div className="flex items-center justify-between w-full">
                                    <span>{product.name}</span>
                                    <span className="text-sm text-muted-foreground">
                                      {formatCurrency(product.priceMin)}
                                    </span>
                                  </div>
                                </CommandItem>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-sm text-muted-foreground">
                                No products found
                              </div>
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
                <CommandItem
                  onSelect={() =>
                    (
                      window as unknown as {
                        goM: (
                          path: string,
                          fn: string,
                          jobId: string,
                          jobSector: string
                        ) => void;
                      }
                    ).goM(
                      "raes-quoteCalculator",
                      "upload_Hover1",
                      jobId?.toString() || "",
                      "RS"
                    )
                  }
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  Upload Hover Data
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Hidden file input for Hover JSON upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="application/json"
        onChange={handleFileSelect}
      />

      {/* Order Confirmation Card */}
      {showConfirmDialog && selectedProduct && orderAddress && (
        <Card className="mt-2 w-full max-w-[500px] shadow-lg mb-16">
          <CardHeader className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute w-8 h-8 top-2 right-2"
              onClick={() => setShowConfirmDialog(false)}
            >
              <X className="w-4 h-4" />
            </Button>
            <CardTitle>Confirm EagleView Order</CardTitle>
            <CardDescription>
              Please review and confirm your EagleView order details.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Product Selection Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="product-select">Product</Label>
              <Select
                value={selectedProduct?.productID?.toString()}
                onValueChange={handleProductChange}
              >
                <SelectTrigger id="product-select" className="w-full">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem
                      key={product.productID}
                      value={product.productID?.toString() || ""}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span>{product.name}</span>
                        <span className="ml-4 text-sm text-muted-foreground">
                          {formatCurrency(product.priceMin)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                className="col-span-3"
                value={orderAddress.address}
                disabled
              />
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div className="flex flex-col col-span-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  className="col-span-3"
                  value={orderAddress.city}
                  disabled
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  className="col-span-1"
                  value={orderAddress.state}
                  disabled
                />
              </div>
              <div className="flex flex-col">
                <Label htmlFor="zip">Zip</Label>
                <Input
                  id="zip"
                  className="col-span-1"
                  value={orderAddress.zip}
                  disabled
                />
              </div>
            </div>

            <div className="p-4 mt-4 space-y-2 border rounded-lg">
              <div className="flex justify-between">
                <span className="font-medium">Product</span>
                <span>{selectedProduct.name}</span>
              </div>

              <div className="flex justify-between">
                <span className="font-medium">Product Price</span>
                <span>{formatCurrency(selectedProduct.priceMin)}</span>
              </div>

              {selectedProduct.deliveryProducts?.[0] && (
                <div className="flex justify-between">
                  <span className="font-medium">Delivery Method</span>
                  <span>{selectedProduct.deliveryProducts[0].name}</span>
                </div>
              )}

              {selectedProduct.addOnProducts &&
                selectedProduct.addOnProducts.length > 0 && (
                  <div className="pt-3 mt-4 border-t">
                    <p className="mb-2 font-medium">Add-On Products</p>
                    <div className="space-y-2">
                      {selectedProduct.addOnProducts.map((addOn) => (
                        <div
                          className="flex items-center justify-between"
                          key={addOn.productID}
                        >
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`add-on-${addOn.productID}`}
                              checked={
                                addOn.productID !== null &&
                                addOn.productID !== undefined &&
                                selectedAddOns.includes(addOn.productID)
                              }
                              onCheckedChange={() =>
                                handleAddOnToggle(addOn.productID)
                              }
                              disabled={
                                selectedAddOns.length > 0 &&
                                addOn.productID !== null &&
                                addOn.productID !== undefined &&
                                !selectedAddOns.includes(addOn.productID)
                              }
                            />
                            <Label
                              htmlFor={`add-on-${addOn.productID}`}
                              className="mb-0 text-sm cursor-pointer"
                            >
                              {addOn.name}
                            </Label>
                          </div>
                          <span className="text-sm">
                            {formatCurrency(addOn.priceMin)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              <div className="flex justify-between pt-2 mt-2 border-t">
                <span className="font-medium">Total</span>
                <span className="font-bold">
                  {formatCurrency(calculateTotal())}
                </span>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePlaceOrder}
              disabled={
                loading ||
                !orderAddress.address ||
                !orderAddress.city ||
                !orderAddress.state ||
                !orderAddress.zip
              }
            >
              {loading ? "Processing..." : "Place Order"}
            </Button>
          </CardFooter>
        </Card>
      )}
      {eagleViewReports.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <FileText className="w-5 h-5 mr-2" />
              EagleView Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {eagleViewReports.map((report) => (
                <li
                  key={report.ReportId}
                  className="flex items-center justify-between p-3 border rounded-md bg-muted/20"
                >
                  <div className="flex items-center space-x-3">
                    {report.Status === "Completed" ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-500" />
                    )}
                    <div>
                      <span className="block text-sm font-medium">
                        Report ID: {report.ReportId}
                      </span>
                      {report.DatePlaced && (
                        <>
                          <span className="block text-xs text-muted-foreground">
                            Report Date:{" "}
                            {new Date(report.DatePlaced).toLocaleDateString()}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            Status: {report.Status}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {report.ReportDownloadLink && (
                      <Button
                        type="button"
                        size="xs"
                        onClick={() => syncMeasurements(report)}
                        className="inline-flex items-center px-3 py-1 text-xs font-medium"
                        title="Sync Measurements"
                      >
                        <UploadIcon className="w-3 h-3 mr-1" />
                        Sync Measurements
                      </Button>
                    )}
                    {report.ReportDownloadLink && (
                      <a
                        href={report.ReportDownloadLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 border border-blue-300 rounded-md bg-blue-50 hover:bg-blue-100"
                        title="Download Report"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const TakeoffImporterWebComponentWrapper: React.FC<
  TakeoffImporterProps & {
    _domElement?: HTMLElement;
  }
> = (props) => {
  return <TakeoffImporter {...props} />;
};

setCustomElementMetadata({
  component: TakeoffImporterWebComponentWrapper,
  tagName: "raes-takeoff-importer",
  observedAttributes: {
    "api-url": (value: string) => ({ value, prop: "apiUrl" }),
    "api-stage": (value: string) => ({ value, prop: "apiStage" }),
    "company-id": (value: string) => ({
      ...customElementAttributeMappers.number(value),
      prop: "companyId",
    }),
    "job-id": (value: string) => ({
      ...customElementAttributeMappers.number(value),
      prop: "jobId",
    }),
    "eagle-view-at": (value: string) => ({ value, prop: "eagleViewAt" }),
    "eagle-view-rt": (value: string) => ({ value, prop: "eagleViewRt" }),
    "placeholder-text": (value: string) => ({ value, prop: "placeholderText" }),
    "empty-message": (value: string) => ({ value, prop: "emptyMessage" }),
    "order-address": (value: string) => {
      try {
        return {
          value: JSON.parse(decodeURIComponent(value)) as OrderAddress,
          prop: "orderAddress",
        };
      } catch (error) {
        console.error("Failed to parse order address:", error);
        return { value: undefined, prop: "orderAddress" };
      }
    },
    width: (value: string) => ({ value, prop: "width" }),
  },
});

export { TakeoffImporterWebComponentWrapper };
